import { useRef, useState, type ReactNode } from 'react';
import {
  CAMPUS_CHOIX,
  type CampusAvatar, type CampusCatalog, type CampusChoixKey, type CampusCoucheKey, type CampusItem,
  type CampusTeinte, type CampusVetementKey,
} from '@campus/core';
import { Button, Tabs, cx } from '../ds/index.js';
import { PcPreview } from './PcPreview.js';

/**
 * Éditeur d'avatar « Campus » (monde du campus, apparence v3). Deux types
 * d'avatar en onglets principaux : « Modèles » (les persos du catalogue et
 * leurs couleurs) et « Personnalisé » (`perso: "sur-mesure"` : coiffure,
 * barbe, couvre-chef, lunettes, vêtements, puis les teintes). Règles reprises du panneau « Mon avatar » de
 * Charles (design system monde-du-campus, composants PanneauAvatar et
 * AvatarSurMesure). L'API compose l'aperçu ; on ne stocke que `{ perso, choix, couches }`.
 */

export const SUR_MESURE = 'sur-mesure';

const ROLE_LABELS: Record<CampusChoixKey, string> = {
  peau: 'Peau', cheveux: 'Couleur des cheveux', haut: 'Haut', bas: 'Bas', chaussures: 'Chaussures',
};
/** Une monture sombre se confond avec les yeux à cette taille : l'or et les couleurs vives d'abord. */
const MONTURES_D_ABORD = ['or', 'rouge', 'bleu', 'rose', 'vert'];

type Kind = 'modeles' | 'personnalise';
type TabId = 'silhouette' | 'couleurs' | 'corps' | 'tete' | 'tenue';
const SUB_TABS: Record<Kind, Array<{ id: TabId; label: string }>> = {
  modeles: [{ id: 'silhouette', label: 'Silhouette' }, { id: 'couleurs', label: 'Couleurs' }],
  personnalise: [{ id: 'corps', label: 'Corps' }, { id: 'tete', label: 'Tête' }, { id: 'tenue', label: 'Tenue' }],
};

// ── Hasard (règles de Charles, `avatarAuHasard` du prototype) ──────────────

const pick = <T,>(t: readonly T[]): T => t[Math.floor(Math.random() * t.length)]!;
const hexRgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const near = (a: number[], b: number[]) => Math.hypot(a[0]! - b[0]!, a[1]! - b[1]!, a[2]! - b[2]!) < 70;

/**
 * Teintes au hasard. Jamais un haut ou un bas proche de la peau (de loin,
 * l'avatar aurait l'air nu) : peau, haut et bas sont donc toujours tirés.
 */
function randomChoix(catalog: CampusCatalog): Partial<Record<CampusChoixKey, string>> {
  const choix: Partial<Record<CampusChoixKey, string>> = {};
  for (const role of CAMPUS_CHOIX) {
    const t = catalog.teintes[role];
    if (t.length > 0 && Math.random() >= 0.35) choix[role] = pick(t).id;
  }
  const peaux = catalog.teintes.peau;
  if (peaux.length === 0) return choix;
  const peau = pick(peaux);
  choix.peau = peau.id;
  for (const role of ['haut', 'bas'] as const) {
    const ok = catalog.teintes[role].filter((t) => !near(hexRgb(t.color), hexRgb(peau.color)));
    if (ok.length > 0) choix[role] = pick(ok).id;
  }
  return choix;
}

/**
 * Un avatar au hasard, du type demandé (sinon : un personnalisé sur deux).
 * Personnalisé : coiffure presque toujours, barbe une fois sur cinq (sa couleur à part une
 * fois sur trois), couvre-chef une fois sur trois, lunettes une fois sur
 * trois, chaque vêtement une fois sur deux.
 */
export function randomCampus(catalog: CampusCatalog, kind?: Kind): CampusAvatar {
  const choix = randomChoix(catalog);
  const K = catalog.surMesure;
  const custom = kind ? kind === 'personnalise' : Math.random() >= 0.5;
  if (!K || !custom) return { perso: pick(catalog.persos).id, choix };
  const one = (t: CampusItem[], p: number) => (t.length > 0 && Math.random() < p ? pick(t).id : '');
  const cheveux = catalog.teintes.cheveux;
  if (!choix.cheveux && cheveux.length > 0) choix.cheveux = pick(cheveux).id;
  return {
    perso: SUR_MESURE,
    choix,
    couches: {
      cheveux: one(K.cheveux, 0.94),
      barbe: one(K.barbes, 0.2),
      teinteBarbe: Math.random() < 0.3 && cheveux.length > 0 ? pick(cheveux).id : '',
      chef: one(K.chefs, 0.35),
      tissu: Math.random() < 0.4 || catalog.teintes.haut.length === 0 ? 'origine' : pick(catalog.teintes.haut).id,
      lunettes: one(K.lunettes, 0.3),
      monture: K.montures.length > 0 ? pick(K.montures).id : '',
      haut: one(K.vetements.haut, 0.5),
      bas: one(K.vetements.bas, 0.5),
      chaussures: one(K.vetements.chaussures, 0.5),
    },
  };
}

export function defaultCampus(catalog: CampusCatalog): CampusAvatar {
  return { perso: catalog.persos[0]!.id, choix: {} };
}

// ── Lignes de l'éditeur ─────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span className="pl-field-label">{label}</span>
      {children}
    </section>
  );
}

/** Pastilles de texte (une seule choisie) ; `none` = option vide en tête (« Aucune », « Veste »…). */
function ChoiceRow({ label, items, value, onPick, none }: {
  label: string;
  items: CampusItem[];
  value: string;
  onPick: (id: string) => void;
  none?: string;
}) {
  const options = none !== undefined ? [{ id: '', label: none }, ...items] : items;
  return (
    <Row label={label}>
      <div role="radiogroup" aria-label={label} className="pl-choices">
        {options.map((o) => (
          <button key={o.id || 'none'} type="button" role="radio" aria-checked={value === o.id} className="pl-choice"
            onClick={() => onPick(o.id)}>
            {o.label}
          </button>
        ))}
      </div>
    </Row>
  );
}

/**
 * Teintes : d'abord l'option « de base » (`base` : « D'origine », « Comme les
 * cheveux »…), puis une pastille par teinte, peinte du ton du milieu de sa rampe.
 */
function TeinteRow({ label, teintes, value, onPick, base }: {
  label: string;
  teintes: Array<{ id: string; label: string; color: string }>;
  value: string;
  onPick: (id: string) => void;
  base: { id: string; label: string };
}) {
  const known = teintes.some((t) => t.id === value);
  return (
    <Row label={label}>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '2px 0 2px 2px' }}>
        <button type="button" role="radio" aria-checked={!known} className="pl-choice" onClick={() => onPick(base.id)}>
          {base.label}
        </button>
        {teintes.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={value === t.id}
            aria-label={`${label} : ${t.label}`}
            title={t.label}
            className={cx('pl-swatch', value === t.id && 'pl-swatch-on')}
            style={{ background: t.color }}
            onClick={() => onPick(t.id)}
          />
        ))}
      </div>
    </Row>
  );
}

// ── Éditeur ─────────────────────────────────────────────────────────────────

export function CampusAvatarEditor({ value, catalog, onChange, headers, aside, compact }: {
  value: CampusAvatar;
  catalog: CampusCatalog;
  onChange: (c: CampusAvatar) => void;
  headers: Record<string, string>;
  aside?: ReactNode;
  compact: boolean;
}) {
  const K = catalog.surMesure;
  const kind: Kind = value.perso === SUR_MESURE && K ? 'personnalise' : 'modeles';
  const surMesure = kind === 'personnalise';
  const [tabs, setTabs] = useState<Record<Kind, TabId>>({ modeles: 'silhouette', personnalise: 'corps' });
  const tab = tabs[kind];
  // Dernier modèle choisi : revenir à « Modèles » le retrouve
  const lastModel = useRef(surMesure ? catalog.persos[0]!.id : value.perso);
  if (!surMesure) lastModel.current = value.perso;

  const choixOf = (k: CampusChoixKey) => value.choix?.[k] ?? 'origine';
  // « D'origine » = rôle absent de `choix` (forme du contrat de Charles)
  const setChoix = (k: CampusChoixKey, v: string) => {
    const choix = { ...value.choix };
    if (v === 'origine') delete choix[k]; else choix[k] = v;
    onChange({ ...value, choix });
  };
  const coucheOf = (k: CampusCoucheKey) => value.couches?.[k] ?? '';
  const setCouche = (k: CampusCoucheKey, v: string) => onChange({ ...value, couches: { ...value.couches, [k]: v } });

  // Les couches restent sur un modèle (le paquet les ignore) : revenir à
  // « Personnalisé » retrouve l'avatar personnalisé d'avant.
  const setKind = (k: Kind) => {
    if (k === kind) return;
    if (k === 'modeles') onChange({ ...value, perso: lastModel.current });
    else onChange({ ...value, perso: SUR_MESURE, couches: value.couches ?? { cheveux: K?.cheveux[0]?.id ?? '' } });
  };

  const teinteRow = (k: CampusChoixKey, label = ROLE_LABELS[k]) => (
    <TeinteRow key={k} label={label} teintes={catalog.teintes[k]} value={choixOf(k)} onPick={(v) => setChoix(k, v)}
      base={{ id: 'origine', label: "D'origine" }} />
  );

  const vetement = (k: CampusVetementKey) => (
    <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {surMesure && K && (
        <ChoiceRow label={ROLE_LABELS[k]} items={K.vetements[k]} value={coucheOf(k)} onPick={(v) => setCouche(k, v)}
          none={K.vetementsBase[k]} />
      )}
      {teinteRow(k, surMesure ? `Couleur - ${ROLE_LABELS[k].toLowerCase()}` : ROLE_LABELS[k])}
    </div>
  );

  const montures = K
    ? [...K.montures].sort((a, b) => {
      const ia = MONTURES_D_ABORD.indexOf(a.id), ib = MONTURES_D_ABORD.indexOf(b.id);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    })
    : [];
  const hairTeintes: CampusTeinte[] = catalog.teintes.cheveux;

  const sections: Record<TabId, ReactNode> = {
    silhouette: (
      <ChoiceRow label="Silhouette" items={catalog.persos} value={value.perso} onPick={(perso) => onChange({ ...value, perso })} />
    ),
    couleurs: CAMPUS_CHOIX.map((k) => teinteRow(k)),
    corps: teinteRow('peau'),
    tete: K && (
      <>
        <ChoiceRow label="Coiffure" items={K.cheveux} value={coucheOf('cheveux')} onPick={(v) => setCouche('cheveux', v)} none="Aucune" />
        {coucheOf('cheveux') && teinteRow('cheveux')}
        <ChoiceRow label="Barbe" items={K.barbes} value={coucheOf('barbe')} onPick={(v) => setCouche('barbe', v)} none="Aucune" />
        {coucheOf('barbe') && (
          <TeinteRow label="Couleur de la barbe" teintes={hairTeintes} value={coucheOf('teinteBarbe')}
            onPick={(v) => setCouche('teinteBarbe', v)} base={{ id: '', label: 'Comme les cheveux' }} />
        )}
        <ChoiceRow label="Couvre-chef" items={K.chefs} value={coucheOf('chef')} onPick={(v) => setCouche('chef', v)} none="Aucun" />
        {coucheOf('chef') && (
          <TeinteRow label="Tissu du couvre-chef" teintes={catalog.teintes.haut} value={coucheOf('tissu') || 'origine'}
            onPick={(v) => setCouche('tissu', v)} base={{ id: 'origine', label: "D'origine" }} />
        )}
        <ChoiceRow label="Lunettes" items={K.lunettes} value={coucheOf('lunettes')} onPick={(v) => setCouche('lunettes', v)} none="Aucunes" />
        {coucheOf('lunettes') && (
          <TeinteRow label="Monture" teintes={montures} value={coucheOf('monture')}
            onPick={(v) => setCouche('monture', v)} base={{ id: '', label: 'Couleur du dessin' }} />
        )}
      </>
    ),
    tenue: (['haut', 'bas', 'chaussures'] as const).map(vetement),
  };

  return (
    <div style={{ display: 'flex', flexDirection: compact ? 'column' : 'row', gap: compact ? 16 : 24, flex: 1, minHeight: 0, color: 'var(--color-text-primary)' }}>
      <section aria-label="Aperçu" style={compact
        ? { display: 'flex', alignItems: 'center', gap: 16 }
        : { width: 184, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          className="pl-page"
          style={{
            width: compact ? 112 : '100%', flexShrink: 0, boxSizing: 'border-box', padding: compact ? '12px 0 4px' : '20px 0 8px',
            borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-default)',
            display: 'flex', justifyContent: 'center',
          }}
        >
          <PcPreview draft={{ v: 3, campus: value }} headers={headers} height={compact ? 84 : 168} />
        </div>
        <div style={{ flex: compact ? 1 : undefined, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {aside}
          <Button icon="shuffle" onClick={() => onChange(randomCampus(catalog, kind))}>Au hasard</Button>
        </div>
      </section>

      <section aria-label="Personnaliser l'avatar" style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {K && (
          <div style={{ overflowX: 'auto' }}>
            <Tabs aria-label="Type d'avatar" items={[{ id: 'modeles', label: 'Modèles' }, { id: 'personnalise', label: 'Personnalisé' }]}
              value={kind} onChange={setKind} />
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <Tabs aria-label="Partie de l'avatar" items={SUB_TABS[kind]} value={tab}
            onChange={(t) => setTabs((old) => ({ ...old, [kind]: t }))} />
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 20,
          // Hauteur : la place qui reste (parent de hauteur limitée), au plus 420 px sur un grand écran
          ...(compact ? {} : { flex: '1 1 auto', minHeight: 160, maxHeight: 420, overflowY: 'auto', paddingRight: 4 }),
        }}>
          {sections[tab]}
        </div>
      </section>
    </div>
  );
}
